/**
 * PRD.329 §8–9 — laboratory test selection, laid out like the paper
 * requisition sheet: department pill tabs, then one bordered "form" panel per
 * department with a dense multi-column grid of square checkboxes.
 *
 * Shared by every ordering surface (Laboratory requisition, Patient Workspace
 * Labs, Consultation Labs). `groupRequisitionCatalog` (unit-tested) decides what
 * is orderable — lab test + active service + active-or-no department — and the
 * order: departments by sort order, tests by name, "Other / Unassigned" last.
 * Tests from several departments still go into ONE order; the pills and panels
 * only organise the list.
 *
 * Selection and per-test specimen/details notes are owned by the caller, so
 * switching tabs or searching never loses a selection. Specimen / details are
 * entered in the "Selected tests" list below the sheet, not inside the grid,
 * which keeps the grid as compact as the paper form.
 */
import { useMemo, useState, type CSSProperties } from 'react';
import {
  ALL_LAB_DEPARTMENTS_TAB,
  groupRequisitionCatalog,
  requisitionDepartmentTabs,
  resolveCatalogDepartment,
  UNASSIGNED_LAB_DEPARTMENT_LABEL,
  type LabTestCatalogRow,
} from '@helios/data-access';
import { NavIcon } from '../../shell/shared';

// ─── Form-sheet styling ───

/**
 * Header tints echoing the colour-coded paper sheets (yellow / pink / blue…).
 * Presentation only: assigned by the department's POSITION in the picker, never
 * stored, so no catalog field doubles as display metadata (PRD.329 R7).
 */
const SHEET_TONES = [
  { accent: '#a16207', tint: '#fefce8', rule: '#fde68a' }, // yellow
  { accent: '#be185d', tint: '#fdf2f8', rule: '#fbcfe8' }, // pink
  { accent: '#0369a1', tint: '#f0f9ff', rule: '#bae6fd' }, // blue
  { accent: '#047857', tint: '#ecfdf5', rule: '#a7f3d0' }, // green
  { accent: '#6d28d9', tint: '#f5f3ff', rule: '#ddd6fe' }, // violet
] as const;
const UNASSIGNED_TONE = { accent: '#475569', tint: '#f8fafc', rule: '#e2e8f0' };

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  columnGap: 'var(--sp-4)',
  rowGap: 2,
  padding: '10px var(--sp-3) 12px',
};

const noteInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: '5px 10px',
  fontSize: 'var(--font-size-xs)',
  borderRadius: 'var(--radius-sm, 6px)',
  border: '1px solid var(--border)',
  outline: 'none',
  boxSizing: 'border-box',
  background: 'var(--bg-surface)',
};

function Pill({
  label,
  total,
  selected,
  active,
  onClick,
}: {
  label: string;
  total: number;
  selected: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 30,
        padding: '0 12px',
        borderRadius: 'var(--radius-full)',
        border: `1px solid ${active ? 'var(--color-primary)' : 'var(--border)'}`,
        background: active ? 'var(--color-primary)' : 'var(--bg-surface)',
        color: active ? '#fff' : 'var(--text)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: active ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {label}
      <span style={{ opacity: 0.75, fontSize: 'var(--font-size-xs)' }}>{total}</span>
      {selected > 0 && (
        <span
          aria-label={`${selected} selected`}
          style={{
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            borderRadius: 'var(--radius-full)',
            background: active ? '#fff' : 'var(--color-primary)',
            color: active ? 'var(--color-primary)' : '#fff',
            fontSize: 11,
            fontWeight: 700,
            lineHeight: '18px',
            textAlign: 'center',
          }}
        >
          {selected}
        </span>
      )}
    </button>
  );
}

/** A paper-style checkbox: square, dark outline, a tick when selected. */
function FormCheckbox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 15,
        height: 15,
        flexShrink: 0,
        marginTop: 2,
        border: `1.5px solid ${checked ? 'var(--color-primary)' : '#94a3b8'}`,
        borderRadius: 2,
        background: checked ? 'var(--color-primary)' : 'transparent',
        color: '#fff',
        fontSize: 11,
        lineHeight: '12px',
        textAlign: 'center',
        fontWeight: 700,
      }}
    >
      {checked ? '✓' : ''}
    </span>
  );
}

export interface LabRequisitionTestPickerProps {
  catalog: readonly LabTestCatalogRow[];
  selected: ReadonlySet<string>;
  onToggle: (serviceId: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** Per-test specimen / details, keyed by service id (→ lab_order_tests.notes). */
  testNotes: Readonly<Record<string, string>>;
  onTestNoteChange: (serviceId: string, value: string) => void;
  formatMoney: (major: number) => string;
  /**
   * Cap the sheets' height and scroll them internally (narrow in-chart
   * drawers). Omit to let the surrounding drawer scroll the whole page.
   */
  maxHeight?: number;
}

export function LabRequisitionTestPicker({
  catalog,
  selected,
  onToggle,
  search,
  onSearchChange,
  testNotes,
  onTestNoteChange,
  formatMoney,
  maxHeight,
}: LabRequisitionTestPickerProps) {
  const [activeTab, setActiveTab] = useState<string>(ALL_LAB_DEPARTMENTS_TAB);

  // Pill counts come from the UNSEARCHED catalog, so they don't jump while typing.
  const allGroups = useMemo(() => groupRequisitionCatalog(catalog), [catalog]);
  const tabs = useMemo(() => requisitionDepartmentTabs(allGroups, selected), [allGroups, selected]);
  // A tab whose department was disabled mid-session falls back to All.
  const tab = tabs.some((t) => t.key === activeTab) ? activeTab : ALL_LAB_DEPARTMENTS_TAB;

  // Stable tone per department, taken from its position in the UNSEARCHED list.
  const toneByKey = useMemo(() => {
    const map = new Map<string, (typeof SHEET_TONES)[number] | typeof UNASSIGNED_TONE>();
    allGroups.forEach((g, i) =>
      map.set(g.key, g.departmentId ? SHEET_TONES[i % SHEET_TONES.length] : UNASSIGNED_TONE),
    );
    return map;
  }, [allGroups]);

  const searchedGroups = useMemo(() => groupRequisitionCatalog(catalog, search), [catalog, search]);
  const shownGroups =
    tab === ALL_LAB_DEPARTMENTS_TAB ? searchedGroups : searchedGroups.filter((g) => g.key === tab);

  const byId = useMemo(() => new Map(catalog.map((t) => [t.id, t] as const)), [catalog]);
  const selectedRows = useMemo(
    () => [...selected].map((id) => byId.get(id)).filter((t): t is LabTestCatalogRow => !!t),
    [selected, byId],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {/* Pills + search stay pinned while the sheets scroll under them. */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
          padding: '4px 0 8px',
          background: 'var(--bg-surface, #fff)',
        }}
      >
        {/* Department pills */}
        <div
          role="tablist"
          aria-label="Laboratory departments"
          style={{ display: 'flex', gap: 'var(--sp-2)', overflowX: 'auto', paddingBottom: 2 }}
        >
          {tabs.map((t) => (
            <Pill
              key={t.key}
              label={t.label}
              total={t.total}
              selected={t.selected}
              active={t.key === tab}
              onClick={() => setActiveTab(t.key)}
            />
          ))}
        </div>

        {/* Search */}
        <div style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              pointerEvents: 'none',
              color: 'var(--text-muted)',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={
              tab === ALL_LAB_DEPARTMENTS_TAB
                ? 'Search tests by name or code…'
                : `Search ${tabs.find((t) => t.key === tab)?.label ?? ''} tests…`
            }
            aria-label="Search laboratory tests"
            style={{
              width: '100%',
              padding: '8px 12px 8px 40px',
              fontSize: 'var(--font-size-sm)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* The form sheet(s). Sheets never shrink: in a height-capped column they
          would otherwise clip their own grids. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          ...(maxHeight ? { maxHeight, overflowY: 'auto' as const, paddingRight: 2 } : {}),
        }}
      >
        {shownGroups.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-4)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              border: '1px dashed var(--border)',
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
            }}
          >
            {search.trim() ? 'No tests match your search.' : 'No orderable laboratory tests.'}
          </div>
        ) : (
          shownGroups.map((group) => {
            const picked = group.tests.filter((t) => selected.has(t.id)).length;
            const tone = toneByKey.get(group.key) ?? UNASSIGNED_TONE;
            return (
              <section
                key={group.key}
                aria-label={group.label}
                style={{
                  flexShrink: 0,
                  border: `1px solid ${tone.rule}`,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-surface, #fff)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--sp-2)',
                    padding: '9px var(--sp-3)',
                    background: tone.tint,
                    borderBottom: `1px solid ${tone.rule}`,
                    borderLeft: `4px solid ${tone.accent}`,
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: tone.accent,
                    }}
                  >
                    {group.departmentId ? `Department of ${group.label}` : group.label}
                  </span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {group.tests.length} test{group.tests.length === 1 ? '' : 's'}
                    {picked > 0 && (
                      <strong style={{ color: 'var(--color-primary)' }}>
                        {' '}
                        · {picked} selected
                      </strong>
                    )}
                  </span>
                </div>
                <div style={gridStyle}>
                  {group.tests.map((t) => {
                    const checked = selected.has(t.id);
                    const price =
                      t.price_cents != null ? formatMoney(t.price_cents / 100) : 'No price set';
                    return (
                      <button
                        key={t.id}
                        type="button"
                        role="checkbox"
                        aria-checked={checked}
                        onClick={() => onToggle(t.id)}
                        title={[t.name, t.external_code, price].filter(Boolean).join(' · ')}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 8,
                          minWidth: 0,
                          padding: '6px 6px',
                          textAlign: 'left',
                          background: checked ? 'var(--bg-surface-2)' : 'transparent',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                        }}
                      >
                        <FormCheckbox checked={checked} />
                        <span
                          style={{
                            minWidth: 0,
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--text)',
                            lineHeight: 1.35,
                            fontWeight: checked ? 'var(--font-weight-semi)' : undefined,
                            // Wrap long names (up to two lines) instead of cutting them off.
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {t.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })
        )}
      </div>

      {/* Selected tests — price, department and per-test specimen / details */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-surface-2)',
        }}
      >
        <div
          style={{
            padding: '8px var(--sp-3)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            color: selectedRows.length ? 'var(--color-primary)' : 'var(--text-muted)',
            borderBottom: selectedRows.length ? '1px solid var(--border)' : 'none',
          }}
        >
          {selectedRows.length === 0
            ? 'No tests selected yet — tick them on the sheet above.'
            : `Selected tests (${selectedRows.length})`}
        </div>
        {selectedRows.map((t) => {
          const dept = resolveCatalogDepartment(t)?.name ?? UNASSIGNED_LAB_DEPARTMENT_LABEL;
          return (
            <div
              key={t.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: '6px var(--sp-3)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <span style={{ width: 200, minWidth: 0, flexShrink: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.name}
                </span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {dept} ·{' '}
                  {t.price_cents != null ? formatMoney(t.price_cents / 100) : 'No price set'}
                </span>
              </span>
              <input
                value={testNotes[t.id] ?? ''}
                onChange={(e) => onTestNoteChange(t.id, e.target.value)}
                placeholder="Specimen / details (optional)"
                aria-label={`Specimen or details for ${t.name}`}
                style={noteInputStyle}
              />
              <button
                type="button"
                onClick={() => onToggle(t.id)}
                aria-label={`Remove ${t.name}`}
                title="Remove"
                style={{
                  flexShrink: 0,
                  width: 26,
                  height: 26,
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm, 6px)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
